import React, { useEffect, useRef, useState } from 'react';
import './Calculator.scss';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

function Calculator() {
  const data = [
    { date: 'Oct 2021', rate: 3.12 },
    { date: 'Jan 2022', rate: 3.51 },
    { date: 'Apr 2022', rate: 5.05 },
    { date: 'Jul 2022', rate: 5.48 },
    { date: 'Oct 2022', rate: 6.98 }
  ];
  const [mortgage, setMortgage] = useState('200000');
  const [current, setCurrent] = useState(3);

  const buyRef = useRef();
  const rentRef = useRef();
  useEffect(() => {
    gsap.matchMedia({
      '(max-width: 767px)': function () {
        const tl2 = gsap.timeline({
          scrollTrigger: {
            trigger: rentRef.current,
            start: 'top 75%',
            end: 'top 75%',
            once: true
          }
        });
        tl2.to('#rent', { rotation: 20, duration: 0.5, reversed: true });
      },

      '(min-width: 768px)': function () {
        const tl2 = gsap.timeline({
          scrollTrigger: {
            trigger: rentRef.current,
            start: 'top 75%',
            end: 'top 75%',
            once: true
          }
        });
        tl2.to('#rent', {
          rotation: 20,
          duration: 0.5,
          delay: 1,
          reversed: true
        });
      }
    });

    const tl1 = gsap.timeline({
      scrollTrigger: {
        trigger: buyRef.current,
        start: 'top 75%',
        end: 'top 75%',
        once: true
      }
    });
    tl1
      .to('#dollar', { opacity: 1, duration: 0.5 })
      .to('#dollar', { scale: 1, duration: 1.5 }, '-=1')
      .to('#dollar', { rotation: 360, duration: 0.7, transformOrigin: '50% 50%' }, '-=1');
  }, []);

  const handleInputChange = (e) => {
    let target = e.target;
    const min = target.min;
    const max = target.max;
    const val = target.value;

    target.style.backgroundSize = ((val - min) * 100) / (max - min) + '% 100%';
  };

  const calculateMortgage = () => {
    const mort = Number(mortgage);
    const int = Number(data[current].rate) / 100 / 12;
    const year = (2052 - new Date().getFullYear()) * 12;
    const now = (mort * int * Math.pow(1 + int, year)) / (Math.pow(1 + int, year) - 1);

    return Number(now.toFixed(2)).toLocaleString(undefined, { minimumFractionDigits: 2 });
  };

  return (
    <div className="calculator">
      <div className="calculator__copy">
        <h2 className="calculator__copy--title">The Power of Percentages</h2>
        <p className="calculator__copy--body">
          Use the calculator below to see the impact of rising mortgage rates on 30-year fixed
          mortgage payments over time.
        </p>
      </div>

      <div className="calculator__main__section" ref={buyRef}>
        <svg
          id="Layer_1"
          data-name="Layer 1"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 50 50"
          className="calculator__main__section__icon"
        >
          <path
            id="house-cal"
            className="cls-1C"
            d="M49.21,25.33,25.88.61a1.72,1.72,0,0,0-1.21-.5,1.7,1.7,0,0,0-1.21.5l-9.7,10.27V5.12a1.31,1.31,0,0,0-.08-.43,1.23,1.23,0,0,0-.61-.61A1.31,1.31,0,0,0,12.64,4H6.83A1.11,1.11,0,0,0,6,4.33a1.13,1.13,0,0,0-.33.79V19.41L.12,25.33a1.64,1.64,0,0,0-.45,1.14,1.71,1.71,0,0,0,.44,1.14,1.77,1.77,0,0,0,.58.4,1.84,1.84,0,0,0,.69.13H5.71v20.3a1.73,1.73,0,0,0,.12.64,1.68,1.68,0,0,0,.37.54,1.42,1.42,0,0,0,.54.36,1.55,1.55,0,0,0,.63.13H42a1.67,1.67,0,0,0,1.67-1.67V28.14H48a1.77,1.77,0,0,0,.69-.13,1.66,1.66,0,0,0,1-1.54A1.65,1.65,0,0,0,49.21,25.33Z"
            transform="translate(0.33 -0.11)"
          />
          <path
            id="dollar"
            className="cls-2C"
            d="M25.07,23.46c5.89,1.21,8.87,3.63,8.87,7.18S30.75,37,26.52,37.7v3.94h-3.7V37.7c-4.23-.66-7.43-3.58-7.43-7.06H19.1c0,2.06,2.55,3.81,5.57,3.81s5.57-1.75,5.57-3.81c0-.65,0-2.63-6-3.86s-8.87-3.63-8.87-7.18,3.2-6.4,7.43-7.07v-4h3.7v4c4.23.67,7.42,3.59,7.42,7.07h-3.7c0-2.06-2.55-3.81-5.57-3.81S19.1,17.54,19.1,19.6C19.1,20.25,19.1,22.22,25.07,23.46Z"
            transform="translate(0.33 -0.11)"
          />
        </svg>
        <p className="calculator__main__section__headers">Mortgage Loan Amount</p>
        <p className="calculator__main__section__numbers">${Number(mortgage).toLocaleString()}</p>
        <input
          className="calculator__main__section__slider calculator__main__section__slider-mortgage"
          type="range"
          min="100000"
          max="1000000"
          step="100000"
          value={mortgage}
          onChange={(e) => {
            handleInputChange(e);
            setMortgage(e.target.value);
          }}
        />

        <p className="calculator__main__section__headers">Start Date</p>
        <p className="calculator__main__section__numbers">{data[current].date}</p>
        <input
          className="calculator__main__section__slider calculator__main__section__slider-date"
          type="range"
          step="1"
          min="0"
          max="4"
          value={current}
          onChange={(e) => {
            handleInputChange(e);
            setCurrent(e.target.value);
          }}
        />

        <div className="calculator__divider" />
        <p className="calculator__main__section__headers">Interest Rate</p>
        <p className="calculator__main__section__numbers-big">{data[current].rate}%</p>
        <p className="calculator__main__section__headers">Average Mortgage Payment per Month</p>
        <p className="calculator__main__section__numbers-big">${calculateMortgage()}</p>
      </div>

      <div className="calculator__captions captions">Source: NAR, 2022</div>
    </div>
  );
}

export default Calculator;
